import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import studNoteLogo from '@/assets/logo.svg';
import studNoteLogoDark from '@/assets/logo-dark.svg';
import { Moon, Sun, Menu } from "lucide-react";
import { useTheme } from "next-themes";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLogoClick } from "@/hooks/useLogoClick";

export const Navigation = () => {
  const location = useLocation();
  const [user, setUser] = useState<any>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { handleLogoClick, isSpinning } = useLogoClick();
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="border-b border-border bg-card dark:bg-card/95 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-all group" onClick={handleLogoClick}>
            <div className={`h-12 sm:h-14 w-12 sm:w-14 flex items-center justify-center transition-transform duration-700 ${isSpinning ? 'animate-[spin_0.7s_ease-in-out]' : ''}`}>
              <img
                src={theme === 'dark' ? studNoteLogoDark : studNoteLogo}
                alt="StudNote - AI lecture recording and note-taking software for students"
                className="h-10 w-10 object-contain"
                width="40"
                height="40"
                loading="eager"
              />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">StudNote</h1>
                <span className="text-[10px] sm:text-xs font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">beta</span>
              </div>
              <p className="text-xs text-muted-foreground hidden sm:block">AI-powered study assistant</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className={`text-sm transition-colors ${
                isActive('/') 
                  ? 'text-foreground font-medium' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/faq" 
              className={`text-sm transition-colors ${
                isActive('/faq') 
                  ? 'text-foreground font-medium' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              FAQ
            </Link>
            <Link
              to="/#pricing"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Pricing
            </Link>
            <Link
              to="/support"
              className={`text-sm transition-colors ${
                isActive('/support')
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Support
            </Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="rounded-full"
            >
              <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <span className="sr-only">Toggle theme</span>
            </Button>
            
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <Button onClick={() => navigate('/app')}>
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate('/app')} className="hidden sm:inline-flex">
                    Sign In
                  </Button>
                  <Button onClick={() => navigate('/app')}>
                    Get Started Free
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-card">
                <nav className="flex flex-col gap-6 mt-8">
                  <Link 
                    to="/" 
                    className={`text-lg transition-colors ${
                      isActive('/') 
                        ? 'text-foreground font-medium' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Home
                  </Link>
                  <Link 
                    to="/faq" 
                    className={`text-lg transition-colors ${
                      isActive('/faq') 
                        ? 'text-foreground font-medium' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    FAQ
                  </Link>
                  <Link
                    to="/#pricing"
                    className="text-lg text-muted-foreground hover:text-foreground transition-colors"
                    onClick={(e) => {
                      setMobileMenuOpen(false);
                      if (location.pathname === '/') {
                        e.preventDefault();
                        document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/support"
                    className={`text-lg transition-colors ${
                      isActive('/support')
                        ? 'text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Support
                  </Link>

                  <div className="border-t border-border pt-6 flex flex-col gap-3">
                    {user ? (
                      <Button onClick={() => navigate('/app')} className="w-full">
                        Go to Dashboard
                      </Button>
                    ) : (
                      <>
                        <Button variant="outline" onClick={() => navigate('/app')} className="w-full">
                          Sign In
                        </Button>
                        <Button onClick={() => navigate('/app')} className="w-full">
                          Get Started Free
                        </Button>
                      </>
                    )}
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};